import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Icon, ICONS } from '@/lib/icons'
import { cn } from '@/lib/utils'
import type { Group, GroupStage, GroupStageMatch, KnockoutBracketState, Player, Sport } from '@/types/api'

interface GroupStageViewProps {
  stage: GroupStage
  playersById: Record<string, Player>
  sport: Sport
  onReportMatch?: (groupId: string, match: GroupStageMatch) => void
  onEditMatch?: (groupId: string, match: GroupStageMatch) => void
  onAdvance?: () => void
  onCompleteGroupStage?: () => void
  advanceLoading?: boolean
  bracketState?: KnockoutBracketState
}

export function GroupStageView({
  stage,
  playersById,
  sport,
  onReportMatch,
  onEditMatch,
  onAdvance,
  onCompleteGroupStage,
  advanceLoading,
  bracketState
}: GroupStageViewProps) {
  const { t } = useTranslation()

  const qualifiedPlayers = useMemo(() => {
    if (!stage?.groups?.length || stage.advancePerGroup <= 0) {
      return [] as { id: string; name: string }[]
    }

    const fallbackName = t('leaderboard.unknownPlayer', 'Unknown player')

    return stage.groups.flatMap(group => {
      const sortedStandings = [...group.standings].sort((a, b) => a.rank - b.rank)
      return sortedStandings
        .filter(standing => standing.rank > 0)
        .slice(0, stage.advancePerGroup)
        .map(standing => {
          const playerId = standing.playerId || `${group.id}-rank-${standing.rank}`
          const playerName = playerId ? playersById[playerId]?.displayName : undefined
          return {
            id: playerId,
            name: playerName && playerName.trim().length > 0 ? playerName : fallbackName,
          }
        })
    })
  }, [playersById, stage.advancePerGroup, stage.groups, t])

  const renderStateBadge = () => {
    switch (stage.state) {
      case 'GROUP_STAGE_STATE_ACTIVE':
        return <Badge variant="secondary">{t('groupStage.view.active', 'In progress')}</Badge>
      case 'GROUP_STAGE_STATE_COMPLETED':
        return <Badge>{t('groupStage.view.completed', 'Completed')}</Badge>
      case 'GROUP_STAGE_STATE_PENDING':
        return <Badge variant="outline">{t('groupStage.view.pending', 'Pending')}</Badge>
      default:
        return <Badge variant="outline">{t('groupStage.view.pending', 'Pending')}</Badge>
    }
  }

  const canAdvance =
    stage.state === 'GROUP_STAGE_STATE_COMPLETED' &&
    bracketState === 'KNOCKOUT_BRACKET_STATE_DRAFT' &&
    Boolean(onAdvance)
  
  // Check if all matches are completed
  const allMatchesCompleted = stage.groups.every(group => 
    group.matches.every(match => match.completed)
  )
  
  const canComplete = allMatchesCompleted && stage.state === 'GROUP_STAGE_STATE_ACTIVE' && Boolean(onCompleteGroupStage)

  return (
    <Card className="shadow-sm">
      <CardHeader>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle>{t('groupStage.view.title', 'Group stage')}</CardTitle>
            <CardDescription>
              {t(
                'groupStage.view.description',
                '{{groups}} groups with {{players}} players total. Top {{advance}} advance to knockout.',
                {
                  groups: stage.groups.length,
                  players: stage.groups.reduce((total, group) => total + group.playerIds.length, 0),
                  advance: stage.advancePerGroup
                }
              )}
            </CardDescription>
          </div>
          <div className="flex items-center gap-3">
            {renderStateBadge()}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Points System Info Box */}
        <Alert className="bg-info-muted border-info/30 text-info-muted-foreground">
          <Icon icon={ICONS.info} className="h-4 w-4 text-info" />
          <AlertTitle>{t('groupStage.view.pointsSystem', 'Point system')}</AlertTitle>
          <AlertDescription>
            <div className="space-y-1 text-sm">
              {sport === 'SPORT_CHESS' ? (
                <>
                  <p className="font-semibold">{t('groupStage.view.pointsChess', 'Win = 1.0pt, Draw = 0.5pt, Loss = 0.0pt')}</p>
                  <p className="text-xs">{t('groupStage.view.tiebreakers', 'Sorting: Points → Head-to-head → Goal difference → Number of wins')}</p>
                </>
              ) : (
                <>
                  <p className="font-semibold">{t('groupStage.view.pointsRacket', 'Win = 2pts, Draw = 1pt, Loss = 0pts')}</p>
                  <p className="text-xs">{t('groupStage.view.tiebreakers', 'Sorting: Points → Head-to-head → Goal difference → Number of wins')}</p>
                </>
              )}
            </div>
          </AlertDescription>
        </Alert>

        {canAdvance && (
          <Alert>
            <AlertTitle>{t('groupStage.advancement.readyTitle', 'Group stage completed!')}</AlertTitle>
            <AlertDescription>
              <div className="space-y-4">
                <p>
                  {t('groupStage.advancement.readyDescription', 'Advance the top {{count}} players from each group to the knockout stage.', {
                    count: stage.advancePerGroup,
                  })}
                </p>

                {qualifiedPlayers.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-semibold">{t('groupStage.advancement.qualifiers', 'Qualified players:')}</h4>
                    <ul className="list-inside list-disc text-sm">
                      {qualifiedPlayers.map(player => (
                        <li key={player.id}>{player.name}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <Button
                  onClick={onAdvance}
                  disabled={advanceLoading}
                  size="lg"
                  className="w-full"
                >
                  <Icon icon={ICONS.arrowRight} />
                  <span className="ml-2">
                    {advanceLoading
                      ? t('groupStage.view.advancing', 'Generating bracket...')
                      : t('groupStage.advancement.advanceButton', 'Advance to knockout stage')}
                  </span>
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Complete Group Stage Button */}
        {canComplete && (
          <div className="p-4 bg-success-muted border border-success/30 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon icon={ICONS.checkCircle} width={20} height={20} className="text-success" />
                <span className="text-sm font-medium text-success-foreground">
                  {t('tournament.complete.groupStage.warning')}
                </span>
              </div>
              <Button
                onClick={onCompleteGroupStage}
                className="bg-success hover:bg-success/90 text-success-foreground"
              >
                {t('tournament.complete.groupStage.button')}
              </Button>
            </div>
          </div>
        )}

        {stage.state === 'GROUP_STAGE_STATE_COMPLETED' && (
          <div className="p-4 bg-info-muted border border-info/30 rounded-lg text-info-muted-foreground">
            <div className="flex items-center gap-2">
              <Icon icon={ICONS.checkCircle} width={20} height={20} className="text-info" />
              <span className="text-sm font-medium">
                {t('tournament.complete.groupStage.completed')}
              </span>
            </div>
          </div>
        )}

        {stage.groups.map(group => (
          <GroupCard
            key={group.id}
            group={group}
            stage={stage}
            playersById={playersById}
            sport={sport}
            onReportMatch={onReportMatch}
            onEditMatch={onEditMatch}
          />
        ))}
      </CardContent>
    </Card>
  )
}

interface GroupCardProps {
  group: Group
  stage: GroupStage
  playersById: Record<string, Player>
  sport: Sport
  onReportMatch?: (groupId: string, match: GroupStageMatch) => void
  onEditMatch?: (groupId: string, match: GroupStageMatch) => void
}

function GroupCard({ group, stage, playersById, sport, onReportMatch, onEditMatch }: GroupCardProps) {
  const { t } = useTranslation()
  const advancingThreshold = stage.advancePerGroup

  const getPlayerName = (playerId: string | undefined) => {
    if (!playerId) return t('groupStage.view.tbd', 'TBD')
    return playersById[playerId]?.displayName ?? t('players.unknownPlayer', 'Unknown player')
  }

  // Returns sport-appropriate header: "Goals" for hockey/foosball, "Sets" for racket sports
  const getScoreColumnHeader = () => {
    if (sport === 'SPORT_TABLE_HOCKEY' || sport === 'SPORT_FOOSBALL') {
      return t('groupStage.view.goals', 'Goals')
    }
    return t('groupStage.view.sets', 'Sets')
  }

  return (
    <div className="rounded-lg border bg-card">
      <div className="flex items-center justify-between border-b p-4">
        <h3 className="text-base font-semibold">{group.name}</h3>
        <Badge variant="outline">
          {t('groupStage.view.groupSummary', '{{players}} players', {
            players: group.playerIds.length
          })}
        </Badge>
      </div>
      <div className="grid gap-4 p-4 lg:grid-cols-2">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('groupStage.view.player', 'Player')}</TableHead>
                <TableHead className="text-right">{t('groupStage.view.points', 'Pts')}</TableHead>
                <TableHead className="text-right">{t('groupStage.view.wins', 'W')}</TableHead>
                <TableHead className="text-right">{t('groupStage.view.losses', 'L')}</TableHead>
                <TableHead className="text-right">{t('groupStage.view.draws', 'D')}</TableHead>
                <TableHead className="text-right">{getScoreColumnHeader()}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {group.standings.map(standing => {
                const isQualified = standing.rank <= advancingThreshold
                return (
                  <TableRow
                    key={standing.playerId}
                    className={cn(isQualified && 'bg-muted/60 font-semibold')}
                  >
                    <TableCell>{getPlayerName(standing.playerId)}</TableCell>
                    <TableCell className="text-right">{standing.points}</TableCell>
                    <TableCell className="text-right">{standing.wins}</TableCell>
                    <TableCell className="text-right">{standing.losses}</TableCell>
                    <TableCell className="text-right">{standing.draws}</TableCell>
                    <TableCell className="text-right">
                      {standing.scoreFor}:{standing.scoreAgainst}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>

        <div className="space-y-3">
          <h4 className="text-sm font-semibold">
            {t('groupStage.view.matchesHeading', 'Matches')}
          </h4>
          <div className="space-y-2">
            {group.matches.map(match => {
              const playerAName = getPlayerName(match.playerAId)
              const playerBName = getPlayerName(match.playerBId)
              const isCompleted = match.completed && match.scoreA !== undefined && match.scoreB !== undefined
              const winnerId = match.winnerId
              const isLocked = Boolean(match.isLocked)
              const lockMessage = isLocked
                ? t(
                    match.lockedReason ? `match.locked.reasons.${match.lockedReason}` : 'match.locked.fallback',
                    t('match.locked.fallback', 'This match cannot be edited right now.')
                  )
                : undefined

              return (
                <div
                  key={match.id}
                  className="flex items-center justify-between rounded-md border bg-background p-3"
                >
                  <div className="space-y-1">
                    <p className="text-sm font-medium">
                      {playerAName} {t('groupStage.view.vs', 'vs')} {playerBName}
                    </p>
                    {isCompleted ? (
                      <p className="text-xs text-muted-foreground">
                        {winnerId
                          ? `${t('groupStage.view.winner', 'Winner')}: ${getPlayerName(winnerId)}`
                          : t('matches.outcome.draw', 'Draw')
                        }
                      </p>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        {t('groupStage.view.notPlayed', 'Awaiting result')}
                      </p>
                    )}
                    {isLocked && (
                      <p className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Icon icon={ICONS.lock} width={14} height={14} />
                        <span>{lockMessage}</span>
                      </p>
                    )}
                  </div>
                  {!isCompleted && onReportMatch && !isLocked && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => onReportMatch(group.id, match)}
                    >
                      <Icon icon={ICONS.edit} width={16} height={16} className="mr-1" />
                      {t('groupStage.view.report', 'Report')}
                    </Button>
                  )}
                  {isCompleted && onEditMatch && !isLocked && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => onEditMatch(group.id, match)}
                    >
                      <Icon icon={ICONS.edit} width={16} height={16} className="mr-1" />
                      {t('common.edit')}
                    </Button>
                  )}
                  {isCompleted && (
                    <div className="text-sm font-medium">
                      {match.scoreA} – {match.scoreB}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
